<template>
  <a-modal
    v-if="visible"
    :title="modalTitle"
    :visible="visible"
    :destroy-on-close="true"
    width="580px"
    data-testid="document-request-modal"
    @cancel="closeModal"
  >
    <div
      v-if="loadingExtend || !targetObject"
      class="loading-spin"
      style="height: 650px"
      data-testid="document-request-loading"
    >
      <a-spin />
    </div>

    <a-form-model
      v-else
      layout="horizontal"
      ref="form"
      class="flex gap-2 flex-column"
      :model="form"
      :rules="rules"
      :label-col="{ span: 8 }"
      :wrapper-col="{ span: 16 }"
      data-testid="document-request-form"
    >
      <div class="flex gap-2 flex-column">
        <h3>{{ targetObject?.name }}</h3>

        <template v-if="existedCompleteRequest">
          <a-form-model-item :colon="false" label-align="left" label="Associates" prop="scope.associates">
            <search-associates
              :multiply="true"
              :is-associate-id="true"
              :value.sync="form.scope.associates"
              data-testid="document-request-associates-select"
            />
          </a-form-model-item>
        </template>

        <template v-else
          ><a-form-model-item :colon="false" label-align="left" label="Company Representative" prop="author_id">
            <search-associates
              :is-associate-id="true"
              :value.sync="form.author_id"
              data-testid="document-request-author-select"
            />
          </a-form-model-item>

          <div class="grid" data-testid="document-request-selection-mode">
            <b class="col-6">Selection by Associates</b>
            <div class="col-6 flex justify-content-end">
              <a-radio-group
                v-model="associateFilterType"
                button-style="solid"
                data-testid="document-request-selection-mode-radio"
              >
                <a-radio-button value="all">All</a-radio-button>
                <a-radio-button value="specific">Specific</a-radio-button>
              </a-radio-group>
            </div>
          </div>

          <div v-show="associateFilterType === 'specific'">
            <a-form-model-item :colon="false" label-align="left" label="Associates" prop="scope.associates">
              <search-associates
                :multiply="true"
                :is-associate-id="true"
                :value.sync="form.scope.associates"
                data-testid="document-request-associates-select"
              />
            </a-form-model-item>
          </div>

          <div v-show="associateFilterType === 'all'">
            <div class="flex gap-2 flex-column">
              <a-form-model-item :colon="false" label-align="left" label="Payment Types" prop="scope.payment_types">
                <custom-select
                  :multiple="true"
                  :value.sync="paymentTypeProxy"
                  placeholder="Payment Types"
                  :options="paymentTypes"
                  name="name"
                  optionKey="id"
                  :collapse-all-selected="false"
                  data-testid="document-request-payment-types-select"
                />
              </a-form-model-item>

              <a-form-model-item :colon="false" label-align="left" label="Positions" prop="scope.roles">
                <custom-select
                  :multiple="true"
                  :value.sync="roleProxy"
                  placeholder="Position"
                  :options="roles"
                  name="name"
                  optionKey="id"
                  :collapse-all-selected="false"
                  data-testid="document-request-positions-select"
                />
              </a-form-model-item>
              <a-form-model-item
                label=" "
                :colon="false"
                label-align="left"
                help="By default, you select the list of entities that will be
                targeted. By using this option, you can target all entities
                except the ones selected in the box."
              >
                <a-checkbox
                  :checked="excludeRolesMode"
                  @change="handleExcludeRolesModeChange"
                  data-testid="document-request-exclude-positions-checkbox"
                >
                  Target All Except Selected
                </a-checkbox>
              </a-form-model-item>

              <a-form-model-item
                :colon="false"
                label-align="left"
                label="Legal Entities"
                prop="scope.legal_entities"
              >
                <custom-select
                  :multiple="true"
                  :value.sync="agencyProxy"
                  placeholder="Legal Entities"
                  :options="legalEntities"
                  name="name"
                  optionKey="id"
                  :collapse-all-selected="false"
                  data-testid="document-request-legal-entities-select"
                />
              </a-form-model-item>
              <a-form-model-item
                label=" "
                :colon="false"
                label-align="left"
                help="By default, you select the list of entities that will be
                targeted. By using this option, you can target all entities
                except the ones selected in the box."
              >
                <a-checkbox
                  :checked="excludeLegalEntitiesMode"
                  @change="handleExcludeLegalEntitiesModeChange"
                  data-testid="document-request-exclude-legal-entities-checkbox"
                >
                  Target All Except Selected
                </a-checkbox>
              </a-form-model-item>

              <a-form-model-item
                v-if="hasContractorPaymentType"
                :colon="false"
                label-align="left"
                label="Contractor Types"
                prop="scope.contractor_sub_types"
              >
                <custom-select
                  :multiple="true"
                  :value.sync="contractorSubTypeProxy"
                  placeholder="Contractor Types"
                  :options="contractorSubTypes"
                  name="name"
                  optionKey="id"
                  :collapse-all-selected="false"
                  data-testid="document-request-contractor-types-select"
                />
              </a-form-model-item>
            </div>
          </div>
        </template>

        <template v-if="!existedCompleteRequest">
          <div class="grid" data-testid="document-request-urgency-escalation">
            <div class="col">
              <p class="text-lg font-bold m-0">Urgency Escalation</p>
              <p>
                Automatically escalates alert urgency: Warning after one day, Critical after two days. Critical alerts
                block shift start until resolved.
              </p>
            </div>
            <div class="col-fixed">
              <a-switch v-model="urgencyFilter" data-testid="document-request-urgency-escalation-switch" />
            </div>
          </div>

          <a-form-model-item
            v-if="!urgencyFilter"
            :colon="false"
            label-align="left"
            label="Urgency"
            prop="severity"
            :extra="SEVERITY_TYPES[form.severity]?.description"
          >
            <custom-select
              :allow-clear="false"
              :value.sync="form.severity"
              placeholder="Urgency"
              :options="urgency"
              name="title"
              optionKey="key"
              data-testid="document-request-urgency-select"
            />
          </a-form-model-item>

          <a-form-model-item :colon="false" label-align="left" label="Notification Source" prop="notification_source">
            <custom-select
              :disabled="form.severity === 'forbid_work'"
              :allow-clear="true"
              :multiple="true"
              :value.sync="form.notification_source"
              placeholder="Notification Source"
              :options="NOTIFICATION_SOURCE"
              name="name"
              optionKey="id"
              data-testid="document-request-notification-source-select"
            />
          </a-form-model-item>
          <a-form-model-item :colon="false" label-align="left" label="Comment" prop="comment">
            <a-textarea v-model="form.comment" placeholder="Comment" data-testid="document-request-comment-input" />
          </a-form-model-item>

          <a-form-model-item
            :colon="false"
            label-align="left"
            :label="targetObject?.type === 'training' ? 'Scheduled Start Date' : 'Scheduled Date'"
          >
            <a-date-picker
              class="w-full"
              format="MM/DD/YYYY"
              :placeholder="targetObject?.type === 'training' ? 'Scheduled Start Date' : 'Scheduled Date'"
              v-model="form.planned_date"
              valueFormat="YYYY-MM-DD"
              :disabled-date="targetObject?.type === 'training' ? e => disabledStartDate(e) : () => false"
              data-testid="document-request-planned-date-input"
            />
          </a-form-model-item>

          <a-form-model-item
            v-if="targetObject?.type === 'training'"
            :colon="false"
            label-align="left"
            label="Scheduled End Date"
            prop="end_date"
          >
            <a-date-picker
              class="w-full"
              format="MM/DD/YYYY"
              valueFormat="YYYY-MM-DD"
              placeholder="Scheduled End Date"
              v-model="form.end_date"
              :disabled="!form.planned_date"
              :disabled-date="disabledEndDate"
              data-testid="document-request-end-date-input"
            /> </a-form-model-item
        ></template>
      </div>
    </a-form-model>

    <template slot="footer">
      <div class="flex justify-content-between" data-testid="document-request-footer">
        <a-button @click="closeModal" data-testid="document-request-cancel-button"> Cancel </a-button>
        <a-button
          icon="save"
          type="primary"
          @click="save"
          :loading="loading"
          data-testid="document-request-submit-button"
        >
          Send
        </a-button>
      </div>
    </template></a-modal
  >
</template>

<script>
import SearchAssociates from '@/components/search-associates.vue'
import CustomSelect from '@/components/ui/custom-select.vue'
import { NOTIFICATION_SOURCE, SEVERITY_TYPES } from '@/store/modules/alerts/constants'
import { CONTRACTOR_SUB_TYPES } from '@/store/modules/legal-entity/constants'
import { deepCopy } from '@/utils/deep-copy'
import { normalizePaymentTypes as normalizePaymentTypeValues } from '@/utils/payment-types'
import {
  Button,
  Checkbox,
  DatePicker,
  FormModel,
  Input,
  Modal,
  notification,
  Radio,
  Spin,
  Switch
} from 'ant-design-vue'
import moment from 'moment-timezone'
import { createNamespacedHelpers } from 'vuex'

const { mapActions } = createNamespacedHelpers('completeRequest')

const initialForm = {
  author_id: undefined,
  severity: 'warning',
  notification_source: ['with_alert'],
  comment: undefined,
  planned_date: null,
  end_date: null,
  scope: {
    legal_entities: [],
    exclude_legal_entities: [],
    contractor_sub_types: [],
    exclude_contractor_sub_types: [],
    roles: [],
    exclude_roles: [],
    payment_types: [],
    exclude_payment_types: [],
    associates: []
  }
}

const initialRules = {
  author_id: [
    {
      required: true,
      message: 'Select company representative',
      trigger: 'change'
    }
  ]
}

export default {
  components: {
    'a-button': Button,
    'a-checkbox': Checkbox,
    'a-textarea': Input.TextArea,
    'a-modal': Modal,
    'a-form-model': FormModel,
    'a-form-model-item': FormModel.Item,
    'a-switch': Switch,
    'a-spin': Spin,
    'a-radio-group': Radio.Group,
    'a-radio-button': Radio.Button,
    'a-date-picker': DatePicker,
    'custom-select': CustomSelect,
    'search-associates': SearchAssociates
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    targetObject: {
      type: Object,
      default: undefined
    },
    specificAssociates: {
      type: Array,
      default: () => []
    },
    type: {
      type: String,
      default: undefined
    },
    allowExtendCheck: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      loading: false,
      loadingExtend: false,

      urgency: Object.entries(SEVERITY_TYPES)
        .map(([key, value]) => ({
          key,
          ...value
        }))
        .filter(item => item.key !== 'normal'),

      form: {
        ...initialForm
      },
      rules: {},
      excludeRolesMode: false,
      excludeLegalEntitiesMode: false,
      isScopeHydrating: false,

      associateFilterType: 'all',
      urgencyFilter: true,

      existedCompleteRequest: undefined,

      SEVERITY_TYPES,
      NOTIFICATION_SOURCE
    }
  },
  watch: {
    visible: {
      async handler(newVal) {
        if (newVal) {
          this.isScopeHydrating = true
          this.excludeRolesMode = false
          this.excludeLegalEntitiesMode = false
          this.associateFilterType = this.specificAssociates.length ? 'specific' : 'all'
          this.form.scope.associates = deepCopy(this.specificAssociates)
          this.form.author_id =
            this.form.author_id ??
            this.$store.state.applicationState.currentProject.dsd[0]?.id ??
            this.$store.state.applicationState.associate?.id

          this.existedCompleteRequest = undefined

          this.fillDefaultForm()

          if (this.targetObject) {
            this.rules = {
              author_id: deepCopy(initialRules.author_id)
            }

            if (this.allowExtendCheck) {
              await this.checkCompleteRequest()
            }
          }
          this.isScopeHydrating = false
        }
      },
      immediate: true
    },
    urgencyFilter: {
      handler(newVal) {
        if (newVal) {
          this.form.severity = 'warning'
        }
      }
    },
    'form.severity': {
      handler(newVal) {
        if (newVal === 'forbid_work') {
          this.form.notification_source = this.NOTIFICATION_SOURCE.map(item => item.id)
        }
      }
    },
    'form.scope.payment_types': {
      handler(newVal, oldVal) {
        if (this.isScopeHydrating) return

        const hasContractor = this.normalizePaymentTypes(newVal).includes('contractor')
        const hadContractor = this.normalizePaymentTypes(oldVal).includes('contractor')

        if (!hasContractor) {
          this.form.scope.contractor_sub_types = []
          return
        }

        if (!hadContractor) {
          this.form.scope.contractor_sub_types = this.contractorSubTypes.map(item => item.id)
        }
      },
      deep: true
    }
  },
  computed: {
    hasContractorPaymentType() {
      return this.paymentTypeProxy.includes('contractor')
    },
    paymentTypeProxy: {
      get() {
        return this.normalizePaymentTypes(this.form.scope.payment_types)
      },
      set(value) {
        this.form.scope.payment_types = this.normalizePaymentTypes(value)
      }
    },
    legalEntities() {
      if (this.targetObject.scope?.exclude_legal_entities?.length) {
        const excluded = this.targetObject.scope.exclude_legal_entities.map(
          item => item.parent_entity ?? item.id ?? item
        )
        return this.$store.state.applicationState.projectLegalEntities.filter(
          item => !excluded.includes(item.parent_entity ?? item.id)
        )
      }
      return this.$store.state.applicationState.projectLegalEntities.filter(item =>
        this.targetObject.scope && this.targetObject.scope.legal_entities.length
          ? this.targetObject.scope.legal_entities
              .map(item => item.parent_entity ?? item.id)
              .includes(item.parent_entity ?? item.id)
          : true
      )
    },
    roles() {
      if (this.targetObject.scope?.exclude_roles?.length) {
        const excluded = this.targetObject.scope.exclude_roles.map(item => item.parent_entity ?? item.id ?? item)
        return this.$store.state.applicationState.projectRoles.filter(
          item => !excluded.includes(item.parent_entity ?? item.id)
        )
      }
      return this.$store.state.applicationState.projectRoles.filter(item =>
        this.targetObject.scope && this.targetObject.scope.roles.length
          ? this.targetObject.scope.roles
              .map(item => item.parent_entity ?? item.id)
              .includes(item.parent_entity ?? item.id)
          : true
      )
    },
    paymentTypes() {
      return this.$store.state.applicationState.paymentTypes
        .map(item => ({
          name: item.name,
          id: item.name.toLowerCase()
        }))
        .filter(item =>
          this.targetObject.scope && this.targetObject.scope.payment_types.length
            ? this.normalizePaymentTypes(this.targetObject.scope.payment_types).includes(item.id)
            : true
        )
    },
    contractorSubTypes() {
      return CONTRACTOR_SUB_TYPES.filter(item =>
        this.targetObject.scope && this.targetObject.scope.contractor_sub_types?.length
          ? this.targetObject.scope.contractor_sub_types.includes(item.id)
          : true
      )
    },

    roleProxy: {
      get() {
        const roles = this.excludeRolesMode ? this.form.scope.exclude_roles : this.form.scope.roles
        return roles ? roles.map(item => item.id) : undefined
      },
      set(value) {
        const mappedValue = value.map(item => ({ id: item }))
        if (this.excludeRolesMode) {
          this.form.scope.exclude_roles = mappedValue
          this.form.scope.roles = []
          return
        }
        this.form.scope.roles = mappedValue
        this.form.scope.exclude_roles = []
      }
    },

    agencyProxy: {
      get() {
        const legalEntities = this.excludeLegalEntitiesMode
          ? this.form.scope.exclude_legal_entities
          : this.form.scope.legal_entities
        return legalEntities || undefined
      },
      set(value) {
        if (this.excludeLegalEntitiesMode) {
          this.form.scope.exclude_legal_entities = value
          this.form.scope.legal_entities = []
          return
        }
        this.form.scope.legal_entities = value
        this.form.scope.exclude_legal_entities = []
      }
    },
    contractorSubTypeProxy: {
      get() {
        return this.form.scope.contractor_sub_types?.length ? this.form.scope.contractor_sub_types : undefined
      },
      set(value) {
        this.form.scope.contractor_sub_types = value
      }
    },
    modalTitle() {
      return this.existedCompleteRequest ? 'Extend Document Request' : 'Create Document Request'
    }
  },
  methods: {
    ...mapActions(['postCreateCompleteRequest', 'postExtendCompleteRequest', 'postCheckCompleteRequest']),
    handleExcludeRolesModeChange(event) {
      if (this.isScopeHydrating) return
      const checked = !!event?.target?.checked
      if (checked === this.excludeRolesMode) return

      this.excludeRolesMode = checked
      if (checked) {
        this.form.scope.exclude_roles = [...this.form.scope.roles]
        this.form.scope.roles = []
        return
      }
      this.form.scope.roles = [...this.form.scope.exclude_roles]
      this.form.scope.exclude_roles = []
    },
    handleExcludeLegalEntitiesModeChange(event) {
      if (this.isScopeHydrating) return
      const checked = !!event?.target?.checked
      if (checked === this.excludeLegalEntitiesMode) return

      this.excludeLegalEntitiesMode = checked
      if (checked) {
        this.form.scope.exclude_legal_entities = [...this.form.scope.legal_entities]
        this.form.scope.legal_entities = []
        return
      }
      this.form.scope.legal_entities = [...this.form.scope.exclude_legal_entities]
      this.form.scope.exclude_legal_entities = []
    },

    closeModal() {
      this.form = deepCopy(initialForm)
      this.rules = {}
      this.associateFilterType = 'all'
      this.excludeRolesMode = false
      this.excludeLegalEntitiesMode = false
      this.$emit('update:visible', false)
    },
    emptyScopeForSpecificAssociates() {
      return {
        roles: [],
        exclude_roles: [],
        legal_entities: [],
        exclude_legal_entities: [],
        contractor_sub_types: [],
        exclude_contractor_sub_types: [],
        payment_types: [],
        exclude_payment_types: [],
        associates: this.form.scope.associates
      }
    },
    normalizeScope(scope) {
      return {
        ...scope,
        roles: this.excludeRolesMode ? [] : (scope.roles ?? []),
        exclude_roles: this.excludeRolesMode ? (scope.exclude_roles ?? []) : [],
        legal_entities: this.excludeLegalEntitiesMode ? [] : (scope.legal_entities ?? []),
        exclude_legal_entities: this.excludeLegalEntitiesMode ? (scope.exclude_legal_entities ?? []) : [],
        payment_types: this.normalizePaymentTypes(scope.payment_types),
        exclude_payment_types: scope.exclude_payment_types ?? [],
        contractor_sub_types: this.hasContractorPaymentType ? (scope.contractor_sub_types ?? []) : [],
        exclude_contractor_sub_types: scope.exclude_contractor_sub_types ?? []
      }
    },
    normalizePaymentTypes(paymentTypes = []) {
      return normalizePaymentTypeValues(paymentTypes, this.$store.state.applicationState.paymentTypes)
    },

    fillDefaultForm() {
      this.form.scope.payment_types = this.paymentTypes.map(item => item.id)
      this.form.scope.exclude_payment_types = []

      this.form.scope.legal_entities = this.legalEntities.map(item => item.id)
      this.form.scope.exclude_legal_entities = []
      this.form.scope.contractor_sub_types = this.hasContractorPaymentType
        ? this.contractorSubTypes.map(item => item.id)
        : []
      this.form.scope.exclude_contractor_sub_types = []

      this.form.scope.roles = this.roles
      this.form.scope.exclude_roles = []
    },

    disabledStartDate(current) {
      return current && current.isSameOrBefore(moment().startOf('day'))
    },

    disabledEndDate(current) {
      if (!this.form.planned_date) {
        return true
      }
      const startDate = moment(this.form.planned_date)
      const minEndDate = startDate.clone().add(1, 'day').startOf('day')
      return current && current.isBefore(minEndDate)
    },

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    save() {
      this.$refs.form.validate(async valid => {
        if (!valid) return

        this.loading = true

        try {
          this.existedCompleteRequest
            ? await this.postExtendCompleteRequest({
                request_id: this.existedCompleteRequest.id,
                author_id: this.form.author_id,
                scope:
                  this.existedCompleteRequest || this.associateFilterType === 'specific'
                    ? this.emptyScopeForSpecificAssociates()
                    : this.normalizeScope(this.form.scope),
                comment: this.form.comment,
                planned_date: this.form.planned_date,
                end_date: this.form.end_date
              })
            : await this.postCreateCompleteRequest({
                complete_request: {
                  ...this.form,
                  severity: !this.urgencyFilter ? this.form.severity : undefined,
                  type: this.type,
                  object_id: this.targetObject.id,
                  scope:
                    this.associateFilterType === 'specific'
                      ? this.emptyScopeForSpecificAssociates()
                      : this.normalizeScope(this.form.scope)
                }
              })

          this.showNotification('success', 'Success', 'Document Request has been created successfully.')

          this.closeModal()
          this.$emit('add')
        } catch (error) {
          this.showNotification('error', 'Error', error.message || error)
        } finally {
          this.loading = false
        }
      })
    },

    async checkCompleteRequest() {
      this.loadingExtend = true

      try {
        const payload = {
          object_id: this.targetObject.id,
          filter: {
            type: this.type
          }
        }

        if (this.type === 'sequence') {
          payload.filter.sequence_type = this.targetObject.type
        }

        const data = await this.postCheckCompleteRequest(payload)

        this.existedCompleteRequest = data?.status === 'cancelled' ? undefined : data
      } catch (error) {
        this.showNotification('error', 'Error', error.message || error)
      } finally {
        this.loadingExtend = false
      }
    }
  }
}
</script>

<style scoped>
::v-deep .ant-modal-body {
  max-height: 600px;
  overflow: auto;
}

.ant-row {
  display: flex;
  margin: 0 !important;
}

.ant-form {
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
}
</style>
